import { useS } from '../../store/game';
import { Panel } from '../../components/ui';
import { GROUPS, WEIGHT, AX, AXN } from '../../content/axes';
import { LOBBIES } from '../../content/lobbies';
import { ISSUES } from '../../content/issues';
import { STATES } from '../../content/states';
import { on } from '../../content/settings';
import { stateMood, approval } from '../../engine/govern';
import { pub, scaleLabel } from '../../engine/opinion';
import { PA } from '../../engine/rules';
import { Sw } from '../../components/ui';
import { sg, dateLabel } from '../../engine/util';

type Tone = 'up' | 'warn' | 'down';
const toneOf = (v: number, lo = 42, hi = 55): Tone => (v >= hi ? 'up' : v < lo ? 'down' : 'warn');

/** Eine Kachel mit großem Wert, Veränderung zum Vormonat, Messbalken und Kommentar. */
function Tile({ name, value, prev, tone, status, foot, color }: { name: string; value: number; prev?: number; tone: Tone; status: string; foot?: React.ReactNode; color?: string }) {
  const d = prev == null ? 0 : value - prev;
  return (
    <div className={'mtile ' + tone}>
      <div className="mt-h"><span className="label" title={name}>{name}</span></div>
      <div className="mt-v"><b className="num">{Math.round(value)}</b>{Math.abs(d) >= .5 && <span className={'num ' + (d > 0 ? 'up' : 'down')}>{d > 0 ? '▲' : '▼'} {sg(d, 0)}</span>}<span className={'mt-s ' + tone}>{status}</span></div>
      <div className="mt-bar" aria-hidden="true"><i style={{ width: `${Math.max(2, Math.min(100, value))}%`, background: color }} /><em /></div>
      {foot && <div className="mt-f sub">{foot}</div>}
    </div>
  );
}

/** Stimmung im Land: Wählergruppen, Verbände, Partei, Länder und Meinungstrends. */
export function Stimmung({ viewer = 'YOU' }: { viewer?: string }) {
  const S = useS();
  const prev: any = S.prev || {};
  const active = ISSUES.filter((i: any) => S.issues[i.id]?.on);
  const pressing = (g: string) => (active.map((i: any) => ({ n: i.n as string, v: (i.imp[g] || 0) * S.issues[i.id].sev })) as { n: string; v: number }[]).filter((x) => x.v > 0).sort((a, b) => b.v - a.v).slice(0, 2).map((x) => x.n);
  const states = Object.keys(STATES).map((id) => ({ id, m: stateMood(id) })).sort((a, b) => b.m - a.m);
  const a = approval();
  const trends = S.pubE ? (AX as string[]).map((x) => ({ x, d: pub(x) - (S.pubE![x] ?? pub(x)) })).filter((t) => Math.abs(t.d) >= .1).sort((p, q) => Math.abs(q.d) - Math.abs(p.d)).slice(0, 6) : [];
  return <>
    <Panel title="Wählergruppen" aside={<>Zustimmung gesamt <b className={'num ' + toneOf(a)}>{Math.round(a)}</b></>}>
      <div className="mtiles">{Object.keys(GROUPS).map((g) => { const v = S.mood[g], t = toneOf(v), pr = pressing(g); return (
        <Tile key={g} name={GROUPS[g]} value={v} prev={prev.mood?.[g]} tone={t} status={t === 'up' ? 'zufrieden' : t === 'down' ? 'unzufrieden' : 'gemischt'}
          foot={<>{Math.round(WEIGHT[g] * 100)} % der Wähler{pr.length ? <> · drückt: {pr.join(', ')}</> : null}</>} />); })}</div>
    </Panel>
    {on('lobby') && <Panel title="Verbände" aside="unter 28: Proteste · über 75: Lob">
      <div className="mtiles">{Object.keys(LOBBIES).map((k) => { const v = S.lob[k], t = toneOf(v, 28, 75); return (
        <Tile key={k} name={LOBBIES[k].n} value={v} prev={prev.lob?.[k]} tone={t} status={v < 28 ? 'Protest droht' : v > 75 ? 'lobt euch' : 'abwartend'} color={v < 28 ? 'var(--bad)' : v > 75 ? 'var(--good)' : undefined} />); })}</div>
    </Panel>}
    {viewer === 'YOU' && on('wings') && <Panel title={`Partei ${S.name}`} aside={S.ptagLeg === S.leg ? 'Parteitag vorbei' : `Parteitag ${dateLabel(S.m0 + 20)}`}>
      <div className="mtiles">{([['soz', 'Sozialer Flügel'], ['wirt', 'Wirtschaftsflügel']] as [string, string][]).map(([k, n]) => { const v = S.wing[k], t = toneOf(v, 25, 60); return (
        <Tile key={k} name={n} value={v} tone={t} status={v < 25 ? 'rebelliert' : v >= 60 ? 'loyal' : 'murrt'} color={v < 25 ? 'var(--bad)' : undefined} />); })}</div>
    </Panel>}
    <div className="cols2">
      <Panel title="Länder">
        <div className="srank">{states.map((x, i) => (i < 4 || i >= states.length - 4) ? (
          <div key={x.id} className="srow"><span><Sw id={S.gov[x.id]} />{STATES[x.id].n} <span className="sub">{PA(S.gov[x.id])}</span></span><span className={'num ' + toneOf(x.m)}>{Math.round(x.m)}</span></div>) : i === 4 ? <div key="gap" className="sub srow">…</div> : null)}</div>
        <span className="sub">Die zufriedensten und unzufriedensten Länder. Die Karte rechts zeigt alle.</span>
      </Panel>
      <Panel title="Meinungstrends seit der Wahl">
        {trends.length ? <div className="srank">{trends.map((t) => <div key={t.x} className="srow"><span>{AXN[t.x]} <span className="sub">{scaleLabel(pub(t.x))}</span></span><span className={'num ' + (t.d > 0 ? 'up' : 'down')}>{t.d > 0 ? '↑ Zustimmung' : '↓ Ablehnung'}</span></div>)}</div>
          : <span className="sub">{S.pubE ? 'Die Meinung zu den Programmthemen ist seit der Wahl stabil.' : 'Trends gibt es ab der ersten Wahl.'}</span>}
      </Panel>
    </div>
  </>;
}
